import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import TopNav from "../components/TopNav.jsx";
import ProductMark from "../components/ProductMark.jsx";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * WhistleBlower — free, anonymous industry-disruption filings.
 *
 * Founder ruling 2026-08-11 (verbatim):
 *   "Is that what you are, a whistleblower? Blow it here, for free, anonymously.
 *   3 AI agents at minimum and potentially the Founder will all review it,
 *   and we'll scream it louder than you ever could, and you remain anonymous —
 *   even WE don't know (or care) who files these. Care to disrupt an industry
 *   with the truth? That's exactly what we ARE, at our core."
 *   — Baker and Sage, by Resilience Multimedia Studios.
 *
 * Anonymity design:
 *   - No login required · no email required · no name required
 *   - No IP logging on this route (server-side · route-level)
 *   - No cookies set
 *   - Filings stored with only a random UUID · no attribution field
 *   - We do not know who filed. We do not care. We amplify.
 */
export default function WhistleblowerPage() {
  const [industry, setIndustry] = useState("");
  const [target, setTarget] = useState("");
  const [story, setStory] = useState("");
  const [evidence, setEvidence] = useState("");
  const [submitted, setSubmitted] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [wall, setWall] = useState({ count: 0, items: [], loading: true });

  useEffect(() => {
    fetch(`${API}/api/sage/whistleblower/wall?limit=50`)
      .then((r) => r.json())
      .then((d) => setWall({ ...d, loading: false }))
      .catch(() => setWall({ count: 0, items: [], loading: false }));
  }, [submitted]);

  const submit = async () => {
    if (story.trim().length < 40) {
      setError("Please tell us at least 40 characters. We need enough to amplify.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/whistleblower/file`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ industry, target, story, evidence }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Filing failed. Try again.");
      setSubmitted(data);
      setIndustry(""); setTarget(""); setStory(""); setEvidence("");
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain relative" data-testid="whistleblower-page">
      <TopNav />
      <div className="mx-auto max-w-4xl px-4 md:px-8 py-12">
        <div className="text-xs uppercase tracking-[0.2em] text-sage-accent mb-2">Free · anonymous · loud</div>
        <div className="flex items-start gap-4 mb-4">
          <div className="text-sage-accent shrink-0"><ProductMark slug="whistleblower" size={52} /></div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter uppercase">
            WhistleBlower
          </h1>
        </div>
        <p className="text-lg md:text-xl text-sage-muted mb-6 leading-relaxed sage-prose">
          Is that what you are? A whistleblower? Blow it here.
        </p>

        <div className="border-l-4 border-sage-accent pl-6 mb-10 sage-prose text-sage-muted">
          <p className="mb-3">
            <strong className="text-sage-text">Free · anonymous · loud.</strong> Three AI agents at minimum — and potentially the Founder himself — will review your filing.
            We will scream it louder than you ever could. You remain anonymous. <strong className="text-sage-text">Even we do not know (or care) who files these.</strong>
          </p>
          <p>
            Care to disrupt an industry with the truth? That's exactly what we ARE, at our core.
          </p>
        </div>

        {/* Anonymity honesty · what we do and don't collect */}
        <div className="border border-sage-border p-5 mb-10" data-testid="anonymity-notice">
          <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">What we do · and do not · collect</div>
          <ul className="text-sm text-sage-muted sage-prose space-y-1">
            <li>· No login · no email · no name · no phone · no address</li>
            <li>· No IP logging on this filing route (server-side, route-level)</li>
            <li>· No cookies set on submission</li>
            <li>· Filings stored under a random UUID with no attribution field</li>
            <li>· We amplify. We publish. We investigate. We do not identify.</li>
          </ul>
        </div>

        {submitted ? (
          <div className="border-2 border-sage-accent p-6 mb-10" data-testid="whistleblower-success">
            <div className="text-xs uppercase tracking-[0.2em] text-sage-accent mb-2">Filed</div>
            <div className="text-2xl font-bold uppercase text-sage-text mb-3">The truth is on record.</div>
            <p className="text-sm text-sage-muted sage-prose mb-3">
              Your filing sits under an anonymous ticket. Three AI agents begin the review inside 24 hours. If it warrants amplification, we amplify. If it warrants investigation, we investigate. If it warrants publication, we publish.
            </p>
            <p className="text-xs text-sage-muted italic">
              Reference (no personal data attached): <code className="bg-sage-pale/40 px-2 py-0.5">{submitted.ticket}</code>
            </p>
            <button
              onClick={() => setSubmitted(null)}
              data-testid="file-another"
              className="mt-4 border border-sage-accent text-sage-accent px-4 py-2 text-xs uppercase tracking-wider hover:bg-sage-accent hover:text-sage-bg transition-colors"
            >
              File another
            </button>
          </div>
        ) : (
          <div className="space-y-4 mb-12" data-testid="whistleblower-form">
            <Field label="Industry (optional)">
              <input
                data-testid="whistleblower-industry"
                type="text"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder="Insurance · Banking · Pharma · Real estate · ..."
                maxLength={80}
                className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
              />
            </Field>
            <Field label="Target (optional · company / entity / person's role)">
              <input
                data-testid="whistleblower-target"
                type="text"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                placeholder="e.g., 'a top-10 US insurer' · 'a regional bank in Missouri'"
                maxLength={160}
                className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
              />
            </Field>
            <Field label="The story · required" required>
              <textarea
                data-testid="whistleblower-story"
                value={story}
                onChange={(e) => setStory(e.target.value)}
                placeholder="What did you see. What is the wrong. Who benefits from silence. What would justice look like."
                rows={8}
                maxLength={8000}
                className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none sage-prose"
              />
              <div className="text-xs text-sage-faint mt-1">{story.length}/8000</div>
            </Field>
            <Field label="Evidence (optional · links · doc URLs · references)">
              <textarea
                data-testid="whistleblower-evidence"
                value={evidence}
                onChange={(e) => setEvidence(e.target.value)}
                placeholder="Links to public docs, filings, court records. Do NOT paste private info you were not authorized to share."
                rows={3}
                maxLength={4000}
                className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
              />
            </Field>
            {error && <div className="text-sm text-sage-warn" data-testid="whistleblower-error">{error}</div>}
            <button
              onClick={submit}
              disabled={busy}
              data-testid="whistleblower-submit"
              className="w-full md:w-auto border-2 border-sage-accent bg-sage-accent text-sage-bg px-8 py-3 text-sm uppercase tracking-wider font-bold hover:bg-sage-text hover:border-sage-text disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {busy ? "Filing…" : "File anonymously"}
            </button>
          </div>
        )}

        <div className="border-t border-sage-border pt-6 text-xs text-sage-muted italic">
          Baker and Sage, by Resilience Multimedia Studios.
        </div>

        {/* ── AUTO-SCREAM WALL · Founder-approved filings only ── */}
        <AutoScreamWall wall={wall} />
      </div>
    </div>
  );
}

function AutoScreamWall({ wall }) {
  return (
    <div className="mt-16 pt-10 border-t-4 border-sage-accent" data-testid="auto-scream-wall">
      <div className="text-xs uppercase tracking-[0.2em] text-sage-accent mb-2">The Auto-Scream Wall</div>
      <h2 className="text-3xl md:text-4xl font-bold tracking-tighter uppercase mb-4">
        When they pass · we scream
      </h2>

      {/* Founder-mandated disclaimer · verbatim */}
      <div className="border-l-4 border-sage-warn pl-6 mb-8" data-testid="scream-disclaimer">
        <div className="text-xs uppercase tracking-[0.2em] text-sage-warn mb-2">Standing disclaimer · read this first</div>
        <p className="text-sm text-sage-muted sage-prose">
          <strong className="text-sage-text">You pass our tests, we blow the whistle.</strong> Sage does not confirm or deny or stand behind these Whistleblower claims — they are simply deemed logical enough to pass our potential validity tests. The truth will be determined by the veracity of the whistleblowers' individual claims. That's not our territory.
        </p>
      </div>

      {/* The four-stage review stack */}
      <div className="mb-8" data-testid="review-stack">
        <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">Every filing runs a four-stage gauntlet before it hits this wall</div>
        <div className="grid md:grid-cols-4 gap-3">
          <ReviewStage n={1} name="Sage" role="Broad review" />
          <ReviewStage n={2} name="Warden" role="Honesty-stack review · no dark patterns" />
          <ReviewStage n={3} name="Verifier" role="Truth · logic · verifiability" />
          <ReviewStage n={4} name="Founder" role="Final catch-all · human signature" />
        </div>
      </div>

      {/* The wall */}
      {wall?.loading ? (
        <div className="text-sm text-sage-muted italic">Loading the wall…</div>
      ) : wall?.count === 0 ? (
        <div className="border border-sage-border p-8 text-center" data-testid="scream-wall-empty">
          <div className="text-sm text-sage-muted italic mb-2">The wall is quiet right now.</div>
          <div className="text-xs text-sage-faint">No filings have completed the four-stage review yet. When they do, they scream here.</div>
        </div>
      ) : (
        <div className="space-y-4" data-testid="scream-wall-items">
          {wall?.items?.map((item) => (
            <ScreamCard key={item.ticket} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewStage({ n, name, role }) {
  return (
    <div className="border border-sage-border p-3" data-testid={`review-stage-${n}`}>
      <div className="text-2xl font-bold text-sage-accent">{n}</div>
      <div className="text-sm font-bold uppercase text-sage-text">{name}</div>
      <div className="text-xs text-sage-muted mt-1">{role}</div>
    </div>
  );
}

function ScreamCard({ item }) {
  return (
    <div className="border-2 border-sage-accent bg-sage-pale/20 p-5" data-testid={`scream-${item.ticket}`}>
      <div className="flex justify-between items-start mb-2 gap-3">
        <div className="text-lg font-bold uppercase text-sage-text">
          {item.industry || "Industry undisclosed"}
        </div>
        <div className="text-[10px] uppercase tracking-[0.15em] text-sage-accent">SCREAMED · {(item.amplified_at || "").slice(0, 10)}</div>
      </div>
      {item.target && (
        <div className="text-sm text-sage-muted italic mb-2">Target: {item.target}</div>
      )}
      <div className="text-base text-sage-text sage-prose mb-3 whitespace-pre-wrap">{item.story}</div>
      {item.evidence && (
        <div className="text-xs text-sage-muted border-t border-sage-border pt-2 mt-2">
          <strong>Referenced evidence:</strong> {item.evidence}
        </div>
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-2">
        {label} {required && <span className="text-sage-accent">*</span>}
      </div>
      {children}
    </label>
  );
}
